import React, { useEffect, useState, useCallback } from "react";
import styled from "styled-components";
import { X, Clock, Calendar } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { getComputerHistory } from "../services/api";

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(4px);
  z-index: 1000;
  display: ${props => props.show ? 'block' : 'none'};
  animation: fadeIn 0.2s ease-in;

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const SidebarContainer = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  width: 60%;
  max-width: 900px;
  height: 100vh;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  box-shadow: -4px 0 20px rgba(0, 0, 0, 0.5);
  z-index: 1001;
  transform: ${props => props.show ? 'translateX(0)' : 'translateX(100%)'};
  transition: transform 0.3s ease-in-out;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const Header = styled.div`
  padding: 24px 32px;
  background: rgba(0, 0, 0, 0.2);
  border-bottom: 2px solid #00eaff;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Title = styled.h2`
  color: #00eaff;
  font-size: 24px;
  font-weight: bold;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Subtitle = styled.p`
  color: #aaa;
  font-size: 14px;
  margin: 4px 0 0 0;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: #fff;
  cursor: pointer;
  padding: 8px;
  border-radius: 50%;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: #00eaff;
  }
`;

const Content = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px 32px;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
  }

  &::-webkit-scrollbar-thumb {
    background: #00eaff;
    border-radius: 4px;
  }
`;

const FilterSection = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 24px;
  flex-wrap: wrap;
`;

const FilterButton = styled.button`
  padding: 10px 20px;
  background: ${props => props.active ? '#00eaff' : 'rgba(255, 255, 255, 0.05)'};
  color: ${props => props.active ? '#000' : '#fff'};
  border: 2px solid ${props => props.active ? '#00eaff' : 'rgba(255, 255, 255, 0.1)'};
  border-radius: 8px;
  cursor: pointer;
  font-weight: bold;
  font-size: 14px;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    background: ${props => props.active ? '#00d4e6' : 'rgba(0, 234, 255, 0.1)'};
    border-color: #00eaff;
  }
`;

const ChartSection = styled.div`
  background: rgba(0, 0, 0, 0.3);
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 24px;
  border: 1px solid rgba(0, 234, 255, 0.2);
`;

const ChartTitle = styled.h3`
  color: #fff;
  font-size: 18px;
  margin: 0 0 16px 0;
  font-weight: bold;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
`;

const StatCard = styled.div`
  background: rgba(0, 0, 0, 0.3);
  border-radius: 12px;
  padding: 20px;
  border: 1px solid rgba(0, 234, 255, 0.2);
`;

const StatLabel = styled.div`
  color: #aaa;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 8px;
`;

const StatValue = styled.div`
  color: ${props => props.color || '#00eaff'};
  font-size: 28px;
  font-weight: bold;
`;

const NoDataMessage = styled.div`
  text-align: center;
  color: #aaa;
  padding: 60px 20px;
  font-size: 16px;
`;

const LoadingMessage = styled.div`
  text-align: center;
  color: #00eaff;
  padding: 60px 20px;
  font-size: 16px;
`;

const HistorySidebar = ({ computer, show, onClose }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState(24);

  const fetchHistory = useCallback(async (hours) => {
    setLoading(true);
    try {
      const data = await getComputerHistory(computer.id, hours);
      // ✅ Garantir que sempre seja um array
      setHistory(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Erro ao buscar histórico:", error);
      setHistory([]); // ✅ Em caso de erro, define como array vazio
    } finally {
      setLoading(false);
    }
  }, [computer?.id]);

  useEffect(() => {
    if (show && computer) {
      fetchHistory(selectedPeriod);
    }
  }, [show, computer, selectedPeriod, fetchHistory]);

  const calculateStats = () => {
    // ✅ Verificação adicional
    if (!Array.isArray(history) || history.length === 0) return null;

    const cpuValues = history.map(h => Number(h.cpu_usage) || 0);
    const memoryValues = history.map(h => Number(h.memory_usage) || 0);
    const diskValues = history.map(h => Number(h.disk_usage) || 0);

    return {
      cpu: {
        avg: (cpuValues.reduce((a, b) => a + b, 0) / cpuValues.length).toFixed(1),
        max: Math.max(...cpuValues).toFixed(1),
        min: Math.min(...cpuValues).toFixed(1),
      },
      memory: {
        avg: (memoryValues.reduce((a, b) => a + b, 0) / memoryValues.length).toFixed(1),
        max: Math.max(...memoryValues).toFixed(1),
        min: Math.min(...memoryValues).toFixed(1),
      },
      disk: {
        avg: (diskValues.reduce((a, b) => a + b, 0) / diskValues.length).toFixed(1),
        max: Math.max(...diskValues).toFixed(1),
        min: Math.min(...diskValues).toFixed(1),
      },
    };
  };

  const formatChartData = () => {
    // ✅ Verificação adicional
    if (!Array.isArray(history)) return [];
    
    return history.map(item => ({
      ...item,
      time: new Date(item.recorded_at).toLocaleTimeString('pt-BR', {
        hour: '2-digit',
        minute: '2-digit'
      }),
      fullTime: new Date(item.recorded_at).toLocaleString('pt-BR'),
    }));
  };

  const stats = calculateStats();
  const chartData = formatChartData();

  if (!computer) return null;

  return (
    <>
      <Overlay show={show} onClick={onClose} />
      <SidebarContainer show={show}>
        <Header>
          <div>
            <Title>
              <Calendar size={28} />
              {computer.custom_name || computer.hostname}
            </Title>
            <Subtitle>Histórico de Monitoramento</Subtitle>
          </div>
          <CloseButton onClick={onClose}>
            <X size={24} />
          </CloseButton>
        </Header>

        <Content>
          <FilterSection>
            <FilterButton 
              active={selectedPeriod === 1}
              onClick={() => setSelectedPeriod(1)}
            >
              <Clock size={16} />
              1 Hora
            </FilterButton>
            <FilterButton 
              active={selectedPeriod === 6}
              onClick={() => setSelectedPeriod(6)}
            >
              <Clock size={16} />
              6 Horas
            </FilterButton>
            <FilterButton 
              active={selectedPeriod === 24}
              onClick={() => setSelectedPeriod(24)}
            >
              <Clock size={16} />
              24 Horas
            </FilterButton>
            <FilterButton 
              active={selectedPeriod === 168}
              onClick={() => setSelectedPeriod(168)}
            >
              <Calendar size={16} />
              7 Dias
            </FilterButton>
          </FilterSection>

          {loading ? (
            <LoadingMessage>⏳ Carregando histórico...</LoadingMessage>
          ) : !Array.isArray(history) || history.length === 0 ? (
            <NoDataMessage>
              📊 Nenhum dado histórico disponível para este período.
              <br />
              <small>Os dados começarão a aparecer assim que o agente enviar informações.</small>
            </NoDataMessage>
          ) : (
            <>
              {stats && (
                <StatsGrid>
                  <StatCard>
                    <StatLabel>CPU Média</StatLabel>
                    <StatValue color="#f9fe2d">{stats.cpu.avg}%</StatValue>
                    <StatLabel style={{ marginTop: '8px', fontSize: '11px' }}>
                      Min: {stats.cpu.min}% | Max: {stats.cpu.max}%
                    </StatLabel>
                  </StatCard>
                  <StatCard>
                    <StatLabel>Memória Média</StatLabel>
                    <StatValue color="#1bb0ce">{stats.memory.avg}%</StatValue>
                    <StatLabel style={{ marginTop: '8px', fontSize: '11px' }}>
                      Min: {stats.memory.min}% | Max: {stats.memory.max}%
                    </StatLabel>
                  </StatCard>
                  <StatCard>
                    <StatLabel>Disco Médio</StatLabel>
                    <StatValue color="#ff5252">{stats.disk.avg}%</StatValue>
                    <StatLabel style={{ marginTop: '8px', fontSize: '11px' }}>
                      Min: {stats.disk.min}% | Max: {stats.disk.max}%
                    </StatLabel>
                  </StatCard>
                </StatsGrid>
              )}

              <ChartSection>
                <ChartTitle>📈 CPU (%)</ChartTitle>
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                    <XAxis 
                      dataKey="time" 
                      tick={{ fontSize: 11, fill: '#aaa' }}
                      stroke="#666"
                    />
                    <YAxis 
                      domain={[0, 100]} 
                      tick={{ fontSize: 11, fill: '#aaa' }}
                      stroke="#666"
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#1a1a2e', 
                        border: '1px solid #00eaff',
                        borderRadius: '8px'
                      }}
                      labelStyle={{ color: '#00eaff' }}
                      formatter={(value) => [`${Number(value).toFixed(1)}%`, 'CPU']}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="cpu_usage" 
                      stroke="#f9fe2d" 
                      strokeWidth={2}
                      dot={{ r: 3, fill: '#f9fe2d' }}
                      activeDot={{ r: 5 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </ChartSection>

              <ChartSection>
                <ChartTitle>💾 Memória (%)</ChartTitle>
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                    <XAxis 
                      dataKey="time" 
                      tick={{ fontSize: 11, fill: '#aaa' }}
                      stroke="#666"
                    />
                    <YAxis 
                      domain={[0, 100]} 
                      tick={{ fontSize: 11, fill: '#aaa' }}
                      stroke="#666"
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#1a1a2e', 
                        border: '1px solid #00eaff',
                        borderRadius: '8px'
                      }}
                      labelStyle={{ color: '#00eaff' }}
                      formatter={(value) => [`${Number(value).toFixed(1)}%`, 'Memória']}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="memory_usage" 
                      stroke="#1bb0ce" 
                      strokeWidth={2}
                      dot={{ r: 3, fill: '#1bb0ce' }}
                      activeDot={{ r: 5 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </ChartSection>

              <ChartSection>
                <ChartTitle>📦 Storage (%)</ChartTitle>
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                    <XAxis 
                      dataKey="time" 
                      tick={{ fontSize: 11, fill: '#aaa' }}
                      stroke="#666"
                    />
                    <YAxis 
                      domain={[0, 100]} 
                      tick={{ fontSize: 11, fill: '#aaa' }}
                      stroke="#666"
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#1a1a2e', 
                        border: '1px solid #00eaff',
                        borderRadius: '8px'
                      }}
                      labelStyle={{ color: '#00eaff' }}
                      formatter={(value) => [`${Number(value).toFixed(1)}%`, 'Storage']}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="disk_usage" 
                      stroke="#ff5252" 
                      strokeWidth={2}
                      dot={{ r: 3, fill: '#ff5252' }}
                      activeDot={{ r: 5 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </ChartSection>
            </>
          )}
        </Content>
      </SidebarContainer>
    </>
  );
};

export default HistorySidebar;